import { useUI } from '@core/context/UIContext';
import { m, AnimatePresence } from 'framer-motion';
import { LogOut, Menu, X, Clock, Unlock, Maximize, Minimize } from 'lucide-react';
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';

import { appConfig } from '../../config/appConfig';
import { useAuth } from '../../context/AuthContext';
import TerminalStepper from '../../../modules/withdrawal/components/TerminalStepper';


/**
 * Navbar — Top navigation bar (Industrial Terminal style).
 * Logo left · Nav links center-left · User + logout right.
 * Mobile: hamburger menu dropdown.
 */
const Navbar = () => {
    const { user, logout, isTerminalMode, setTerminalMode } = useAuth();
    const [mobileOpen, setMobileOpen] = useState(false);
    const ui = useUI();
    const { pageTitle = '', isScrolled = false, setShowUnlockModal, isFullscreen, toggleFullscreen } = useUI();

    const {
        brand: brandConfig,
        navigation: allNavItems,
    } = appConfig;

    const filteredNavItems = allNavItems.filter(item => {
        // 1. Lockdown Filter: Strictly restrict menu items in Kiosk Mode
        if (isTerminalMode) {
            const allowedKioskPaths = ['/terminal', '/audit'];
            return allowedKioskPaths.includes(item.path);
        }

        // 3. Role Filter (Standard Admin Access)
        const userRole = user?.role?.toUpperCase();
        const requiredRole = item.requiredRole?.toUpperCase();
        return !requiredRole || requiredRole === userRole;
    });

    const name = user?.displayName || user?.email || 'Usuario';
    const initials = user?.initials || name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

    return (
        <>
            {/* ── TOP BAR ── */}
            <header className="shrink-0 w-full bg-[#0F172A] border-b border-white/5 flex items-center h-16 px-6 gap-6 z-50 shadow-xl shadow-black/20">

                {/* LOGO & DYNAMIC TITLE */}
                <div className="flex items-center gap-3 shrink-0 mr-4">
                    <img
                        src={brandConfig?.logoFull}
                        alt={brandConfig?.logoAlt}
                        className="h-6 md:h-7 w-auto object-contain transition-all"
                    />
                    
                    <AnimatePresence>
                        {(isScrolled || window.innerWidth < 768) && (
                            <m.div
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -10 }}
                                className="flex items-center gap-2 md:gap-3"
                            >
                                <div className="w-px h-5 bg-white/20" />
                                <h2 className="text-accent font-black uppercase italic tracking-tighter text-[10px] md:text-sm whitespace-nowrap truncate max-w-[120px] md:max-w-[200px] pr-1">
                                    {pageTitle}
                                </h2>
                            </m.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* DIVIDER */}
                <div className="hidden md:block w-px h-8 bg-white/10 shrink-0" />

                {/* NAVIGATION LINKS (Desktop) */}
                <nav className={`hidden md:flex items-center gap-1 ${isTerminalMode ? '' : 'flex-1'}`}>
                    {filteredNavItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            className={({ isActive }) => `
                                flex items-center gap-2.5 px-4 py-2.5 rounded-xl transition-all duration-200 text-[10px] font-black uppercase tracking-[0.15em]
                                ${isActive
                                    ? 'bg-white/10 text-accent border border-white/10 shadow-inner backdrop-blur-xl'
                                    : 'text-white/40 hover:text-white/80 hover:bg-white/5'
                                }
                            `}
                        >
                            {({ isActive }) => (
                                <>
                                    <item.icon size={15} strokeWidth={isActive ? 2.5 : 1.5} />
                                    <span>{item.label}</span>
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                {/* TERMINAL STEPPER (Industrial context) */}
                {isTerminalMode && (
                    <div className="hidden lg:flex flex-1 items-center justify-center px-4">
                        <div className="bg-white/5 border border-white/10 rounded-2xl px-6 py-1 backdrop-blur-md">
                            <TerminalStepper step={ui.terminalStep} terminalMode={ui.terminalMode} isInNavbar={true} />
                        </div>
                    </div>
                )}

                {/* CLOCK DISPLAY (Desktop) */}
                <div className="hidden md:flex items-center gap-3 px-4 h-10 bg-white/5 border border-white/10 rounded-xl shrink-0">
                    <Clock className="text-accent/50" size={14} />
                    <span className="text-[11px] font-mono font-black text-white/90 tracking-widest whitespace-nowrap leading-none">
                        {ui?.currentTime?.toLocaleTimeString([], { hour12: false })}
                    </span>
                </div>

                {/* LOCKDOWN INDICATOR (Desktop) */}
                {isTerminalMode && (
                    <div className="hidden xl:flex items-center gap-2 px-3 h-10 bg-accent/10 border border-accent/20 rounded-xl animate-pulse">
                        <div className="w-2 h-2 rounded-full bg-accent" />
                        <span className="text-[10px] font-black text-accent uppercase tracking-widest">Terminal Mode</span>
                    </div>
                )}

                {/* USER FOOTER (Desktop) */}
                <div className="hidden md:flex items-center gap-3 ml-auto shrink-0">
                    {isTerminalMode && (
                        <div className="flex items-center gap-2">
                            {/* FULLSCREEN TOGGLE */}
                            <button
                                onClick={toggleFullscreen}
                                className="w-10 h-10 flex items-center justify-center bg-white/5 text-accent border border-white/10 rounded-xl hover:scale-105 active:scale-95 transition-all shadow-lg shadow-black/20"
                                title={isFullscreen ? "Minimizar Pantalla" : "Expandir Pantalla"}
                            >
                                {isFullscreen ? <Minimize size={18} strokeWidth={2.5} /> : <Maximize size={18} strokeWidth={2.5} />}
                            </button>

                            {/* UNLOCK BUTTON */}
                            <button
                                onClick={() => setShowUnlockModal(true)}
                                className="w-10 h-10 flex items-center justify-center bg-accent text-primary border border-accent rounded-xl hover:scale-105 active:scale-95 transition-all shadow-lg shadow-accent/20"
                                title="Desbloquear Terminal"
                            >
                                <Unlock size={18} strokeWidth={2.5} />
                            </button>
                        </div>
                    )}

                    {/* USER PILL — hidden when terminal is locked */}
                    {!isTerminalMode && (
                        <div className="flex items-center gap-2.5 px-3 h-10 bg-white/5 border border-white/10 rounded-xl">
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center font-black text-[10px] text-white shrink-0 ${user?.avatarColor || 'bg-white/10'}`}>
                                {initials}
                            </div>
                            <div className="flex flex-col leading-none">
                                <span className="text-[10px] font-black text-white uppercase tracking-tighter truncate max-w-[100px]">{name.split(' ')[0]}</span>
                                <span className="text-[8px] font-bold text-white/30 uppercase tracking-[0.15em] mt-0.5">{user?.role}</span>
                            </div>
                        </div>
                    )}
                    
                    {!isTerminalMode && (
                        <button
                            onClick={logout}
                            className="w-10 h-10 flex items-center justify-center bg-white/5 hover:bg-red-500/10 text-white/20 hover:text-red-400 border border-white/10 hover:border-red-500/20 rounded-xl transition-all active:scale-90"
                            title="Cerrar sesión"
                        >
                            <LogOut size={14} />
                        </button>
                    )}
                </div>

                {/* MOBILE HAMBURGER */}
                <button
                    onClick={() => setMobileOpen(v => !v)}
                    className="md:hidden ml-auto p-2.5 text-white/60 hover:text-white bg-white/5 rounded-xl border border-white/10 transition-all"
                >
                    {mobileOpen ? <X size={18} /> : <Menu size={18} />}
                </button>
            </header>

            {/* ── MOBILE DROPDOWN ── */}
            {mobileOpen && (
                <div className="md:hidden fixed top-16 inset-x-0 z-40 bg-[#0F172A] border-b border-white/10 flex flex-col p-4 gap-2 shadow-2xl animate-in slide-in-from-top-2 duration-200">
                    {filteredNavItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            onClick={() => setMobileOpen(false)}
                            className={({ isActive }) => `
                                flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all text-[10px] font-black uppercase tracking-[0.15em]
                                ${isActive
                                    ? 'bg-white/10 text-accent border border-white/10'
                                    : 'text-white/50 hover:text-white hover:bg-white/5'
                                }
                            `}
                        >
                            {({ isActive }) => (
                                <>
                                    <item.icon size={16} strokeWidth={isActive ? 2.5 : 1.5} />
                                    <span>{item.label}</span>
                                </>
                            )}
                        </NavLink>
                    ))}
                    {isTerminalMode && (
                        <button
                            onClick={() => {
                                setShowUnlockModal(true);
                                setMobileOpen(false);
                            }}
                            className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-accent text-primary border border-accent/20 transition-all text-[10px] font-black uppercase tracking-[0.15em] mb-2 shadow-lg shadow-accent/20 active:scale-95"
                        >
                            <Unlock size={16} strokeWidth={2.5} />
                            <span>Desbloquear Terminal</span>
                        </button>
                    )}
                    <div className="h-px bg-white/5 my-2" />
                    {/* USER SECTION (mobile) — hidden when terminal is locked */}
                    {!isTerminalMode && (
                        <div className="flex items-center justify-between px-4 py-3">
                            <div className="flex items-center gap-3">
                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-black text-sm text-white ${user?.avatarColor || 'bg-white/10'}`}>
                                    {initials}
                                </div>
                                <div>
                                    <p className="text-[10px] font-black text-white uppercase tracking-tight">{name.split(' ')[0]}</p>
                                    <p className="text-[8px] text-white/30 uppercase tracking-widest">{user?.role}</p>
                                </div>
                            </div>
                            <button onClick={logout} className="p-3 bg-white/5 hover:bg-red-500/20 text-white/30 hover:text-red-400 rounded-xl border border-white/5 transition-all">
                                <LogOut size={15} />
                            </button>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default Navbar;
